import { type PendingSave } from "@/features/calendar/calendarFilter/appointmentFilterReducer";
import {
  saveCalendarFilters,
  saveCalendarSettings,
  setZoomWidth,
} from "@repo/api";
import { useEffect, useRef } from "react";

/**
 * Persists calendar settings and filters after the reducer has flagged them via `pendingSave`.
 *
 * The saving cannot live in `appointmentFilterReducer` because React requires reducers to be
 * pure: StrictMode invokes them twice in development, and concurrent rendering may replay or
 * discard a render entirely. Calling the API there sends duplicate (or unwanted) requests --
 * this is what caused two PUT + two OPTIONS calls per filter change. An effect runs after
 * commit, exactly once per committed state, so it is the safe place for the request.
 *
 * `pendingSave` is compared by reference so each new object saves once, and repeated changes
 * to the same field still produce a new object and therefore a new save.
 */
export const useSaveCalendarChanges = (
  pendingSave: PendingSave | undefined
) => {
  const savedRef = useRef<PendingSave | undefined>(undefined);

  useEffect(() => {
    if (!pendingSave || savedRef.current === pendingSave) {
      return;
    }
    savedRef.current = pendingSave;

    if (pendingSave.zoomWidth !== undefined) {
      setZoomWidth(pendingSave.zoomWidth);
    }
    if (pendingSave.calendarSettings) {
      saveCalendarSettings(pendingSave.calendarSettings).catch(() => undefined);
    }
    if (pendingSave.calendarFilters) {
      saveCalendarFilters(pendingSave.calendarFilters).catch(() => undefined);
    }
  }, [pendingSave]);
};
